import React, { useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { useDispatch, useSelector } from "react-redux";
import { AuthSelectors } from "src/redux/Auth/selectors";
import fetchWrapper from "src/utils/fetchWrapper";
import { clientPoolPositionsColumnDefs, defaultColDef } from "./column_defs";
import styles from "./styles.module.scss";
import { TableSelectors } from "src/redux/Table/selectors";

const adjustDetailsData = (data) => {
    return {
        ...data,
        // The details table should look grouped by these three fields
        list_name: "",
        security_name: "",
        ticker: "",
        __hasDetailPanel: false,
    };
};

const getLastRowIndex = (request, response) => {
    if (!response) return undefined;
    return response.lastRow < request.endRow ? response.lastRow : undefined;
};

const syncWithMasterColumnState = (gridRef, masterColumnState) => {
    if (gridRef && gridRef.columnApi) {
        const detailColumnState = masterColumnState
            .map((colState) => {
                if (colState.colId === "num_owners") {
                    return { ...colState, colId: "client_id" };
                }
                return colState;
            })
            .filter((colState) => colState.colId !== "blank_column");

        gridRef.columnApi.applyColumnState({
            state: detailColumnState,
            applyOrder: true,
        });
    }
};

const DetailCellRenderer = ({ data: masterRowData }) => {
    const dispatch = useDispatch();
    const userPositionsUrl = useSelector(AuthSelectors.BuildUserPositionsUrl);
    const masterColumnState = useSelector(TableSelectors.ColumnState);
    const gridRef = useRef();

    const createDetailDatasource = (masterRow) => {
        return {
            getRows: (params) => {
                const requestBody = {
                    ...params.request,
                    filterModel: {
                        list_name: {
                            filterType: "text",
                            type: "equals",
                            filter: masterRow.list_name,
                        },
                        security_id: {
                            filterType: "text",
                            type: "equals",
                            filter: masterRow.security_id,
                        },
                    },
                };

                const body = JSON.stringify(requestBody);

                dispatch(
                    fetchWrapper(userPositionsUrl, {
                        method: "POST",
                        body,
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    })
                )
                    .then((response) => {
                        const data = (response.data || []).map(
                            adjustDetailsData
                        );
                        params.successCallback(
                            data,
                            getLastRowIndex(params.request, response)
                        );
                    })
                    .catch((error) => {
                        console.error("Error fetching detail data:", error);

                        params.failCallback();
                    });
            },
        };
    };

    const onGridReady = (params) => {
        syncWithMasterColumnState(params, masterColumnState);
        const datasource = createDetailDatasource(masterRowData);
        params.api.setServerSideDatasource(datasource);
    };

    const detailColumnDefs = clientPoolPositionsColumnDefs
        .map((col) => {
            if (col.field === "num_owners") {
                // Remove the expand renderer for detail rows
                return {
                    ...col,
                    field: "client_id",
                    cellRenderer: undefined,
                };
            }
            return { ...col };
        })
        .filter((col) => col.field !== "blank_column");

    useEffect(
        () => syncWithMasterColumnState(gridRef.current, masterColumnState),
        [masterColumnState]
    );

    return (
        <div className={`${styles.detailTable} ag-theme-alpine-dark`}>
            <AgGridReact
                ref={gridRef}
                columnDefs={detailColumnDefs}
                defaultColDef={defaultColDef}
                rowModelType="serverSide"
                serverSideStoreType="partial"
                cacheBlockSize={25}
                blockLoadDebounceMillis={300}
                onGridReady={onGridReady}
                headerHeight={0}
                rowHeight={40}
                suppressHorizontalScroll={true}
                alwaysShowVerticalScroll={true}
            />
        </div>
    );
};

export default DetailCellRenderer;
